import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AuthContext from '../shared/AuthContext';

function rowUserId(row) {
  const raw = row?.userId ?? row?.id;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export default function PlatformAdminUsersPanel() {
  const { apiClient, user } = useContext(AuthContext);
  const currentUserId = rowUserId(user);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/get-users');
      setRows(Array.isArray(res.data) ? res.data : []);
      setError('');
    } catch (e) {
      setError(e.response?.status === 403 ? 'Admin access required.' : 'Could not load users.');
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    load();
  }, [load]);

  const setPlatformAdmin = async (targetId, grantAdmin) => {
    setSavingId(targetId);
    setError('');
    try {
      await apiClient.patch(`/api/admin/users/${targetId}/platform-role`, { admin: grantAdmin });
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.response?.data?.detail || 'Could not update admin role.');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <section className="settings-section">
      <h2>Platform administrators</h2>
      <p className="settings-muted">
        Grant <strong>ROLE_ADMIN</strong> for QnaRead console access (bulk upload, user management, all teams).
        Team workspace roles are managed under <Link to="/teams">Teams → Settings</Link>.
      </p>
      <p className="settings-muted">
        Full table: <Link to="/admin/users">Admin → Users</Link>
      </p>
      {error && <p className="settings-error" role="alert">{error}</p>}
      {loading ? (
        <p className="settings-muted">Loading users…</p>
      ) : (
        <div className="settings-admin-user-table-wrap">
          <table className="settings-admin-user-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Platform admin</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 25).map((row) => {
                const userId = rowUserId(row);
                const isAdmin = Boolean(row.platformAdmin || row.roles?.includes('ROLE_ADMIN'));
                return (
                  <tr key={userId ?? row.email}>
                    <td>{row.email}</td>
                    <td>
                      <label className="settings-admin-toggle">
                        <input
                          type="checkbox"
                          checked={isAdmin}
                          disabled={userId == null || savingId === userId}
                          onChange={(e) => setPlatformAdmin(userId, e.target.checked)}
                        />
                        {isAdmin ? 'Admin' : 'User'}
                        {currentUserId === userId && isAdmin && (
                          <span className="settings-muted"> (you)</span>
                        )}
                      </label>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
